import { Badge, Box, Group, Radio, Stack, Text, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";
import { IconCalendar, IconInfoCircle } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DateSlot } from "~/components/date-slot";
import { AppointmentSlot } from "~/types/appointment";
import { getSlotsForDay } from "~/utils/get-slots-for-day";
import { extractDoctorPatterns, generateVirtualSlots } from "~/utils/doctor-patterns";

interface MultiSessionSchedulerProps {
  form: UseFormReturnType<any>;
  slotsData: AppointmentSlot[];
  serviceType: any;
}

export const MultiSessionScheduler = ({
  form,
  slotsData,
  serviceType,
}: MultiSessionSchedulerProps) => {
  const { t, i18n } = useTranslation();
  const sessions = form.values.sessions || [];
  const intervalDays = serviceType?.sessionIntervalDays ?? 0;
  const patterns = extractDoctorPatterns(slotsData);

  return (
    <Stack gap="xl" mt="2rem">
      <Title order={3} fz={18} fw={600}>
        {t("treatment-schedule")}
      </Title>
      <Text fz={14} c="dimmed">
        {t("multi-session-description", { count: sessions.length })}
      </Text>

      {sessions.map((session: any, index: number) => {
        // SEQUENTIAL LOGIC: Only show this session if it's the first OR the previous one is fully booked
        const isFirst = index === 0;
        const prevSession = !isFirst ? sessions[index - 1] : null;
        const isVisible = isFirst || (prevSession?.date && prevSession?.time);

        if (!isVisible) return null;

        // INTERVAL LOGIC: Subsequent sessions must be at least 'interval' days after the previous session
        const minDateForThisSession = prevSession?.date
          ? dayjs(prevSession.date).add(intervalDays, "day").startOf("day")
          : dayjs().startOf("day");

        // Filter available dates based on interval
        const realFutureDates = Array.from(
          new Set(
            slotsData
              ?.filter(
                (item: any) =>
                  dayjs(item.startTime).isSame(minDateForThisSession, "day") ||
                  dayjs(item.startTime).isAfter(minDateForThisSession, "day")
              )
              ?.map((item: any) =>
                dayjs(item.startTime)
                  .set("hours", 0)
                  .set("minutes", 0)
                  .set("seconds", 0)
                  .set("milliseconds", 0)
                  .toISOString()
              )
              .sort(
                (a: string, b: string) =>
                  new Date(a).getTime() - new Date(b).getTime()
              )
          )
        );

        // PREDICTIVE BRANCH: If no real slots, synthesize virtual ones
        let displayDates = realFutureDates.slice(0, 5);
        let isUsingPredictedDates = false;

        if (displayDates.length === 0 && patterns.workingDays.length > 0) {
          isUsingPredictedDates = true;
          let checkDate = minDateForThisSession;
          const virtualDates: string[] = [];
          while (virtualDates.length < 5 && virtualDates.length < 30) {
            if (patterns.workingDays.includes(checkDate.day())) {
              virtualDates.push(checkDate.toISOString());
            }
            checkDate = checkDate.add(1, "day");
          }
          displayDates = virtualDates;
        }

        const dailySlots = getSlotsForDay(slotsData, session.date) || [];
        const virtualSlots = (isUsingPredictedDates && session.date) 
          ? generateVirtualSlots(session.date, patterns) 
          : [];
        const effectiveSlots = dailySlots.length > 0 ? dailySlots : virtualSlots;

        return (
          <div
            key={index}
            style={{
              border: "1px solid #dee2e6",
              padding: "1rem",
              borderRadius: "8px",
              backgroundColor: "#f8f9fa",
            }}>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={700} fz={16} c="primary.7">
                  {t("session-x", { number: session.session })}
                </Text>
                <Group gap="xs">
                  {isUsingPredictedDates && (
                    <Badge color="orange" variant="light" leftSection={<IconInfoCircle size={12} />}>
                      {t("tentative-availability")}
                    </Badge>
                  )}
                  {index === 0 && (
                    <Badge variant="light" color="blue">
                      {t("primary-session")}
                    </Badge>
                  )}
                </Group>
              </Group>

              <Radio.Group
                label={t("service-date")}
                withAsterisk
                value={session.dateType || "slot"}
                onChange={val => {
                  const newSessions = [...sessions];
                  newSessions[index].dateType = val;
                  newSessions[index].date = null; // Reset date when switching types
                  newSessions[index].time = "";
                  newSessions[index].slotId = "";
                  newSessions[index].isPredicted = false;
                  form.setFieldValue("sessions", newSessions);
                }}>
                <Stack gap="xs" mt="0.5rem">
                  <Radio label={t("select-date-slot")} value="slot" size="sm" />
                  {(session.dateType === "slot" || !session.dateType) && (
                    <Box pl="md">
                      <Radio.Group
                        value={
                          session.date
                            ? dayjs(session.date)
                                .set("hours", 0)
                                .set("minutes", 0)
                                .set("seconds", 0)
                                .set("milliseconds", 0)
                                .toISOString()
                            : ""
                        }
                        onChange={val => {
                          const newSessions = [...sessions];
                          newSessions[index].date = new Date(val);
                          newSessions[index].time = "";
                          newSessions[index].slotId = "";
                          newSessions[index].isPredicted = false;

                          // Smart suggestion
                          if (index === 0 && intervalDays > 0) {
                            for (let i = 1; i < newSessions.length; i++) {
                              if (!newSessions[i].date) {
                                const suggestedDate = dayjs(val)
                                  .add(i * intervalDays, "day")
                                  .toDate();
                                newSessions[i].date = suggestedDate;
                                newSessions[i].time = "";
                                newSessions[i].slotId = "";
                                newSessions[i].isPredicted = false;
                              }
                            }
                          }
                          form.setFieldValue("sessions", newSessions);
                        }}>
                        <Group gap="xs">
                          {displayDates.length > 0 ? (
                            displayDates.map((item: any, idx: number) => (
                              <DateSlot key={idx} date={new Date(item)} />
                            ))
                          ) : (
                            <Text fz={13} c="red" fs="italic">
                              {t("no-slots-after-interval", {
                                days: intervalDays,
                              })}
                            </Text>
                          )}
                        </Group>
                        {isUsingPredictedDates && (
                          <Text fz={12} c="orange" mt={4} fs="italic">
                            {t("predicted-slots-hint")}
                          </Text>
                        )}
                      </Radio.Group>
                    </Box>
                  )}

                  <Radio
                    label={t("select-date-manually")}
                    value="manual"
                    size="sm"
                  />
                  {session.dateType === "manual" && (
                    <Box pl="md">
                      <DatePickerInput
                        w={"100%"}
                        maw={350}
                        withAsterisk
                        placeholder={t("select-service-date")}
                        leftSection={<IconCalendar size={16} stroke={1.5} />}
                        value={session.date}
                        onChange={val => {
                          const newSessions = [...sessions];
                          newSessions[index].date = val;
                          newSessions[index].time = "";
                          newSessions[index].slotId = "";
                          newSessions[index].isPredicted = false;

                          // Smart suggestion
                          if (index === 0 && intervalDays > 0 && val) {
                            for (let i = 1; i < newSessions.length; i++) {
                              if (!newSessions[i].date) {
                                const suggestedDate = dayjs(val)
                                  .add(i * intervalDays, "day")
                                  .toDate();
                                newSessions[i].date = suggestedDate;
                                newSessions[i].time = "";
                                newSessions[i].slotId = "";
                                newSessions[i].isPredicted = false;
                              }
                            }
                          }
                          form.setFieldValue("sessions", newSessions);
                        }}
                        minDate={minDateForThisSession.toDate()}
                        excludeDate={(date: Date) => {
                          const isSlotAvailable = slotsData?.some((slot: any) => {
                            const slotDate = new Date(
                              slot.startTime
                            ).toDateString();
                            return slotDate === date.toDateString();
                          });
                          return !isSlotAvailable;
                        }}
                      />
                      <Text fz={12} c="dimmed" mt={4}>
                        {t("more-dates-available-manually")}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Radio.Group>

              <Radio.Group
                label={t("service-time")}
                withAsterisk
                value={session.time}
                onChange={val => {
                  const newSessions = [...sessions];
                  newSessions[index].time = val;
                  const selectedSlot = effectiveSlots.find(
                    slot => slot.startTime === val
                  ) as { id?: string; isPredicted?: boolean } | undefined;
                  newSessions[index].isPredicted =
                    selectedSlot?.isPredicted ?? false;
                  newSessions[index].slotId = selectedSlot?.isPredicted
                    ? ""
                    : selectedSlot?.id ?? "";
                  form.setFieldValue("sessions", newSessions);
                }}>
                {!session.date ? (
                  <Text fz={13} c="dimmed" fs="italic">
                    {t("select-date-for-options")}
                  </Text>
                ) : (
                  <Group gap={"md"} align="start">
                    {effectiveSlots.length > 0 ? (
                      <>
                        <Stack gap={"xs"} align="start">
                          {effectiveSlots
                            ?.slice(0, 3)
                            ?.map((slot: any) => {
                              const startDate = new Date(session.date);
                              startDate.setHours(
                                slot.startHour,
                                slot.startMinute,
                                0,
                                0
                              );
                              const endDate = new Date(session.date);
                              endDate.setHours(slot.endHour, slot.endMinute, 0, 0);

                              return (
                                <Radio
                                  mt={"0.25rem"}
                                  key={slot.startTime}
                                  value={slot.startTime}
                                  label={`${startDate.toLocaleTimeString(
                                    i18n.language,
                                    {
                                      timeStyle: "short",
                                    }
                                  )} - ${endDate.toLocaleTimeString(
                                    i18n.language,
                                    {
                                      timeStyle: "short",
                                    }
                                  )}`}
                                />
                              );
                            })}
                        </Stack>
                        <Stack gap={"xs"} align="start">
                          {effectiveSlots
                            ?.slice(3)
                            ?.map((slot: any) => {
                              const startDate = new Date(session.date);
                              startDate.setHours(
                                slot.startHour,
                                slot.startMinute,
                                0,
                                0
                              );
                              const endDate = new Date(session.date);
                              endDate.setHours(slot.endHour, slot.endMinute, 0, 0);

                              return (
                                <Radio
                                  mt={"0.25rem"}
                                  key={slot.startTime}
                                  value={slot.startTime}
                                  label={`${startDate.toLocaleTimeString(
                                    i18n.language,
                                    {
                                      timeStyle: "short",
                                    }
                                  )} - ${endDate.toLocaleTimeString(
                                    i18n.language,
                                    {
                                      timeStyle: "short",
                                    }
                                  )}`}
                                />
                              );
                            })}
                        </Stack>
                      </>
                    ) : (
                      <Text fz={13} c="dimmed" fs="italic">
                        {t("no-available-slots")}
                      </Text>
                    )}
                  </Group>
                )}
              </Radio.Group>
            </Stack>
          </div>
        );
      })}
    </Stack>
  );
};
